import * as React from 'react';
import { Button, Input, Modal, Select } from '../..';
import { showAlert, useAuth, User } from '../../../context/authContext';
import {
  CashRegister,
  storePreferenceFormAction,
} from '../../../context/storeContext';
import { formatErrors } from '../../../utils/request';
import setToLocalStorage from '../../../utils/setToLocalStorage';

type ModalProps = {
  onCloseModal: () => void;
  openRegisterCb: (register: CashRegister) => void;
  cashRegisters: CashRegister[];
  currencySymbol: string;
  storeId: number;
};

function OpenRegister({
  onCloseModal,
  openRegisterCb,
  cashRegisters,
  currencySymbol,
  storeId,
}: ModalProps) {
  const [{ user }, dispatch] = useAuth();
  const [selectedCashRegister, setSelectedCashRegister] =
    React.useState<CashRegister>();
  const [cashFloat, setCashFloat] = React.useState(NaN);
  const [valueError, setValueError] = React.useState('');
  const [status, setStatus] = React.useState<
    'IDLE' | 'LOADING' | 'RESOLVED' | 'REJECTED'
  >('IDLE');

  React.useEffect(() => {
    setSelectedCashRegister(cashRegisters[0]);
  }, [cashRegisters]);

  const handleRegisterChange = (registerId: string) => {
    const register = cashRegisters.find(
      fndRegister => fndRegister.id.toString() === registerId,
    );
    setSelectedCashRegister(register);
  };

  const handleOpenRegister = async () => {
    if (
      Number.isNaN(cashFloat) ||
      cashFloat === undefined ||
      cashFloat === null
    ) {
      setValueError('Enter a starting cash float');
      return;
    }

    if (!user || !selectedCashRegister) {
      return;
    }

    try {
      setStatus('LOADING');

      const { cashRegister: getCashRegister } =
        (await storePreferenceFormAction(
          `/open-register?store=${storeId}`,
          'post',
          { cashRegisterId: selectedCashRegister.id, cashFloat, storeId },
        )) as any;

      const updateUserStoreCashRegister = {
        ...user,
        ...(user.defaultStore && {
          defaultStore: {
            ...user.defaultStore,
            cashRegisters: user.defaultStore.cashRegisters.map(fndRegister => {
              if (fndRegister.id === selectedCashRegister.id) {
                return getCashRegister;
              }
              return fndRegister;
            }),
          },
        }),
      } as User;

      setStatus('RESOLVED');

      dispatch({ type: 'SET_AUTH_USER', payload: updateUserStoreCashRegister });

      setToLocalStorage(
        '_pos_usrid',
        JSON.stringify(updateUserStoreCashRegister),
      );

      openRegisterCb(getCashRegister);

      onCloseModal();
    } catch (error) {
      setStatus('REJECTED');

      const { message } = formatErrors(error);

      showAlert(dispatch, {
        show: true,
        message,
        type: 'ERROR',
      });
    }
  };

  return (
    <Modal onClose={onCloseModal} show>
      <div className="rounded-md bg-white w-[500px] overflow-hidden">
        <form>
          <div className="flex flex-col p-5">
            <h3
              className="text-pos-black text-[20px] font-semibold mb-2
              inline-flex whitespace-pre items-center"
            >
              Start Register{' '}
              <span className="cursor-pointer text-pos-gray text-xbase font-light ml-1">
                ({selectedCashRegister?.name})
              </span>
            </h3>

            <div className="mt-2 mb-2 flex flex-col space-y-4">
              <div className="w-full">
                <label htmlFor="cashFloat" className="text-xbase block mb-1">
                  Select Register
                </label>
                <Select
                  placeholder={{
                    value: '',
                    label: '',
                  }}
                  value={selectedCashRegister?.id || ''}
                  options={cashRegisters.map(fndRegister => ({
                    value: fndRegister.id,
                    label: fndRegister.name,
                  }))}
                  name="cashRegister"
                  id="cashRegister"
                  onChange={event => handleRegisterChange(event.target.value)}
                />
              </div>
              <div className="w-full">
                <label htmlFor="cashFloat" className="text-xbase block mb-1">
                  Starting Cash
                </label>
                <div className="relative">
                  <Input
                    name="cashFloat"
                    id="cashFloat"
                    value={cashFloat}
                    min={0}
                    type="number"
                    placeholder="0.00"
                    onChange={event => {
                      setCashFloat(event.target.valueAsNumber);
                      setValueError('');
                    }}
                    className="sale-quantity pl-[25px]"
                  />
                  <span className="left-3 absolute top-0 my-auto z-50 bottom-0 text-xbase inline-flex items-center">
                    {currencySymbol}
                  </span>
                </div>
                {valueError && (
                  <div className="text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1">
                    {valueError}
                  </div>
                )}

                <span className="text-pos-gray font-light text-sm mt-2 inline-block leading-normal">
                  Enter your starting cash float to make sales with this
                  register.
                </span>
              </div>
            </div>

            <div className="mt-5 text-center flex flex-row ml-auto space-x-4">
              <Button
                label="Open Register"
                onClick={handleOpenRegister}
                type="button"
                disabled={status === 'LOADING'}
                loading={status === 'LOADING'}
                className="!bg-pos-green border border-[rgba(27,31,36,0.15)] min-w-[150px]  h-[40px]
                items-center justify-center text-white text-xbase shrink-0 font-medium 
                shadow-[0_1px_0_rgba(27,31,36,0.1),inset_0_1px_0_rgba(255,255,255,0.03)]"
              />
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default OpenRegister;
